import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { GlassmorphismContainer } from "../styled/shared/Glassmorphism";
import {
  usePathStore,
  usePointStore,
  useSavePathStore,
  vehicleSpecs,
} from "../../Store/store";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(GlassmorphismContainer)`
  width: 440px;
  min-height: 280px;

  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  padding: 24px 28px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
  margin-bottom: 20px;
`;

const Input = styled.input`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
`;

const TextArea = styled.textarea`
  height: 80px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 12px;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &.primary {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-1px);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
`;

const Select = styled.select`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;
  option {
    background: #303231;
    color: #fff;
  }
`;

const SelectLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
`;

export default function SavePathModal() {
  const {
    isSavePathOpen,
    setIsSavePathOpen,
    pathTitle,
    pathDescription,
    setPathTitle,
    setPathDescription,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
  } = useSavePathStore();
  const { validEquipmentPaths } = usePathStore();
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);

  const titleRef = useRef(null);

  // 경로 저장
  const handleConfirm = useCallback(async () => {
    if (!pathTitle.trim()) {
      // 제목이 비어있으면 포커스
      if (titleRef.current) {
        titleRef.current.focus();
      }
      return;
    }

    setIsSavePathOpen(false);
    if (onConfirm) {
      await onConfirm({
        title: pathTitle,
        description: pathDescription,
        equipmentId:
          selectedEquipmentId || Object.keys(validEquipmentPaths)[0] || "",
      });
    }
  }, [
    onConfirm,
    pathTitle,
    pathDescription,
    setIsSavePathOpen,
    selectedEquipmentId,
  ]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    setIsSavePathOpen(false);
  }, [onCancel, setIsSavePathOpen]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  useEffect(() => {
    if (!isSavePathOpen) return;
    const handler = (e) => {
      if (e.key === "Enter" && e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isSavePathOpen, handleConfirm, handleCancel]);

  useEffect(() => {
    if (isSavePathOpen && titleRef.current) {
      titleRef.current.focus();
    }
  }, [isSavePathOpen]);

  if (!isSavePathOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <Title>경로 저장</Title>
        <Input
          ref={titleRef}
          placeholder="경로 제목을 입력하세요"
          value={pathTitle}
          onChange={(e) => setPathTitle(e.target.value)}
        />
        <TextArea
          placeholder="경로에 대한 간단한 설명을 입력하세요 (선택사항)"
          value={pathDescription}
          onChange={(e) => setPathDescription(e.target.value)}
        />
        <SelectLabel>저장할 경로에 대한 장비 선택</SelectLabel>
        <Select
          onChange={(e) => setSelectedEquipmentId(e.target.value)}
          value={
            selectedEquipmentId || Object.keys(validEquipmentPaths)[0] || ""
          }
        >
          {Object.keys(validEquipmentPaths).map((key) => (
            <option key={key} value={key}>
              {vehicleSpecs[key]?.name}
            </option>
          ))}
        </Select>
        <ButtonRow>
          <Button className="secondary" onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button className="primary" onClick={handleConfirm}>
            {confirmText}
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
